import React, { useState } from 'react';
import { X, Send, Shield, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { OtpResendButton } from './OtpResendButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Two-stage delivery OTP modal used by Agent and Partner views.
 *
 * Stage 1: SEND — call /delivery-otp/send to text a 6-digit OTP to the
 *          customer. Server returns the OTP in mock mode for demos.
 * Stage 2: VERIFY — operator enters the OTP the customer recites, server
 *          validates and marks the booking DELIVERED.
 *
 * Props:
 *   role         'agent' | 'partner'
 *   roleCode     The operator's code (e.g. MUM-AGENT-01)
 *   booking      The booking object (must include booking_id, baggage_tag,
 *                customer_name, customer_phone)
 *   onClose      Called when the user dismisses the modal
 *   onDelivered  Called after a successful verification, used by the parent
 *                to refresh its list
 */
export const DeliveryOtpModal = ({ role, roleCode, booking, onClose, onDelivered }) => {
  const [step, setStep] = useState('idle'); // idle | sending | sent | verifying | done
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null); // { customer_phone_masked, mock_otp? }

  const baseUrl = `${API}/${role}/${roleCode}/bookings/${booking.booking_id}/delivery-otp`;

  const handleSend = async () => {
    setError('');
    setStep('sending');
    try {
      const res = await axios.post(`${baseUrl}/send`);
      setInfo(res.data);
      setStep('sent');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please retry.');
      setStep('idle');
    }
  };

  const handleVerify = async () => {
    setError('');
    if (otp.trim().length !== 6) {
      setError('OTP must be 6 digits.');
      return;
    }
    setStep('verifying');
    try {
      await axios.post(`${baseUrl}/verify`, { otp: otp.trim() });
      setStep('done');
      setTimeout(() => {
        onDelivered?.();
        onClose?.();
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to verify OTP.');
      setStep('sent');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="card-brutalist bg-white p-6 max-w-md w-full" data-testid="delivery-otp-modal">
        <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-black">
          <div className="flex items-center gap-2">
            <Shield size={20} color="#FF4500" />
            <h2 className="text-lg font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
              CONFIRM DELIVERY
            </h2>
          </div>
          <button
            data-testid="delivery-otp-close"
            onClick={onClose}
            className="hover:text-orange-600"
            disabled={step === 'verifying'}
          >
            <X size={22} />
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-100 border-2 border-black">
          <div className="text-xs font-mono uppercase text-gray-600 mb-1">BAGGAGE TAG</div>
          <div className="text-xl font-black font-mono">{booking.baggage_tag}</div>
          <div className="text-xs font-mono text-gray-600 mt-2">
            {booking.customer_name} · {booking.customer_phone || '—'}
          </div>
        </div>

        {step === 'idle' && (
          <div data-testid="delivery-otp-stage-idle">
            {booking.delivery_phone && (
              <div className="mb-3 p-3 border-2 border-black bg-orange-50">
                <div className="text-xs font-mono uppercase text-gray-600 mb-1 flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
                  OTP WILL BE SENT VIA WHATSAPP TO
                </div>
                <div className="text-lg font-black font-mono" data-testid="delivery-otp-target-phone">
                  {booking.delivery_phone}
                </div>
              </div>
            )}
            <p className="text-sm font-mono mb-4 leading-relaxed">
              A 6-digit OTP will be sent to the customer's <b>WhatsApp</b> on the
              number above. Ask them to open WhatsApp and read the OTP out to you
              ONLY after they have physically received and inspected all bags.
              This is the only way to mark the booking as DELIVERED.
            </p>
            {error && (
              <p data-testid="delivery-otp-error" className="text-red-600 text-sm mb-3 font-mono border-2 border-red-600 bg-red-50 p-2">
                {error}
              </p>
            )}
            <button
              data-testid="delivery-otp-send-btn"
              onClick={handleSend}
              className="btn-primary w-full py-3 flex items-center justify-center gap-2"
            >
              <Send size={16} />
              SEND OTP VIA WHATSAPP
            </button>
          </div>
        )}

        {step === 'sending' && (
          <div className="text-center py-6" data-testid="delivery-otp-sending">
            <p className="font-mono text-sm loading-pulse">SENDING WHATSAPP OTP...</p>
          </div>
        )}

        {(step === 'sent' || step === 'verifying') && (
          <div data-testid="delivery-otp-stage-verify">
            <div className="mb-3 p-3 border-2 border-black bg-green-50">
              <div className="text-xs font-mono uppercase mb-1 flex items-center gap-1">
                <CheckCircle2 size={12} color="#00C853" /> OTP SENT VIA WHATSAPP
              </div>
              <p className="text-xs font-mono">
                OTP delivered on WhatsApp to{' '}
                <span className="font-bold" data-testid="delivery-otp-sent-to">
                  {info?.delivery_phone || info?.customer_phone_masked || 'delivery contact'}
                </span>.
                Ask the customer to open WhatsApp and read out the OTP. Valid for {Math.round((info?.expires_in || 600) / 60)} minutes.
              </p>
              {info?.mock_otp && (
                <p className="text-xs font-mono mt-2 p-2 bg-yellow-100 border-2 border-yellow-600" data-testid="delivery-otp-mock-hint">
                  MOCK MODE — OTP: <span className="font-black text-base">{info.mock_otp}</span>
                </p>
              )}
            </div>

            <label className="block text-xs font-mono uppercase tracking-wide mb-2">
              ENTER OTP RECITED BY CUSTOMER
            </label>
            <input
              data-testid="delivery-otp-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit OTP"
              className="w-full mb-3 text-center text-2xl font-mono font-black tracking-[0.4em]"
              autoFocus
            />

            {error && (
              <p data-testid="delivery-otp-error" className="text-red-600 text-sm mb-3 font-mono border-2 border-red-600 bg-red-50 p-2">
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <OtpResendButton
                testId="delivery-otp-resend-btn"
                onResend={handleSend}
                variant="dark"
              />
              <button
                data-testid="delivery-otp-verify-btn"
                onClick={handleVerify}
                disabled={step === 'verifying' || otp.length !== 6}
                className="btn-primary flex-1 py-3 disabled:opacity-50"
              >
                {step === 'verifying' ? 'VERIFYING...' : 'VERIFY & MARK DELIVERED'}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="text-center py-6" data-testid="delivery-otp-success">
            <CheckCircle2 size={48} color="#00C853" className="mx-auto mb-3" />
            <p className="text-lg font-black uppercase">DELIVERED</p>
            <p className="text-xs font-mono text-gray-600 mt-1">Customer notified.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryOtpModal;
